import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MatButtonModule, MatCheckboxModule, MatStepperModule, MatIconModule, MatExpansionModule, MatFormFieldModule, MatInputModule,
  MatListModule, MatRadioModule, MatSelectModule, MatOptionModule, MatTabsModule, MatTooltipModule, MatMenuModule,
  MatGridListModule, MatBadgeModule, MatTableModule, MatPaginatorModule, MatNativeDateModule, MatSortModule
} from '@angular/material';

import { MatDatepickerModule } from '@angular/material/datepicker';

import { ToastrModule, ToastContainerModule } from 'ngx-toastr';
import { NgbModule, NgbActiveModal, NgbDropdown, NgbCarousel } from '@ng-bootstrap/ng-bootstrap';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ChartsModule } from 'ng2-charts';

import { RouterModule, Routes } from '@angular/router';

import { HttpModule } from '@angular/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandlerService } from './services/ErrorHandlerService'
import { HttpClient, HttpClientModule } from '@angular/common/http';

import { Ng2Webstorage } from 'ngx-webstorage';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './login/login.component';
import { NavbarComponent } from './navbar/navbar.component';
import { ModalProfileComponent } from './modal-profile/modal-profile.component';
import { ModalEditProfileComponent } from './modal-edit-profile/modal-edit-profile.component';
import { CheckmailComponent } from './checkmail/checkmail.component';
import { ErrorPageComponent } from './error-page/error-page.component';
import { ModalForgotPasswordComponent } from './modal-forgot-password/modal-forgot-password.component';
import { ResetComponent } from './reset/reset.component';
import { AnalysisComponent } from './analysis/analysis.component';
import { FooterComponent } from './footer/footer.component';
import { HomeFacilityComponent } from './home-facility/home-facility.component';
import { SurveyComponent } from './survey/survey.component';
import { ModalSurveyComponent } from './modal-survey/modal-survey.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { RoiComponent } from './roi/roi.component';
import { MdiComponent } from './mdi/mdi.component';
import { PprComponent } from './ppr/ppr.component';
import { ModalReadMDIComponent } from './modal-read-mdi/modal-read-mdi.component';
import { ModalSurveyRoiComponent } from './modal-survey-roi/modal-survey-roi.component';
import { ModalReadPprComponent } from './modal-read-ppr/modal-read-ppr.component';

import { UserService } from './services/user.service';
import { FacilitiesService } from './services/facilities.service';
import { DashboardComponent } from './dashboard/dashboard.component';
import { PreviousRouteServiceService } from './services/previous-route-service.service';
import { SurveyService } from './services/survey.service';
import { ModalNotificationComponent } from './modal-notification/modal-notification.component';

import { EqualValidatorDirective } from '../shared/equal.validator';
import { AuthGuard } from './auth/auth.guard';
import { SummaryGuard } from './auth/summary.guard';
import { AnalysisGuard } from './auth/analysis.guard';
import { AuthSurveyGuard } from './auth/auth-survey.guard';
import { DashboardGuard } from './auth/dashboard.guard';

import { AgmCoreModule } from '@agm/core';
import { GooglePlacesDirective } from './directive/google-places.directive';

import { ValuesPipe } from './pipe/values.pipe';

import { SidebarModule } from 'ng-sidebar';
import { DragDropDirective } from './directive/drag-drop.directive';
import { AuthInterceptor } from './auth/auth.interceptor';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';

const appRoutes: Routes = [
  {
    path: '',
    redirectTo: 'welcome',
    pathMatch: 'full'
  },
  {
    path: 'welcome',
    component: LoginComponent
  },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [AuthGuard],
    data: { breadcrumb: 'Home' }
  },
  {
    path: 'checkmail/:username/:random',
    component: CheckmailComponent,
  },
  {
    path: 'errorPage',
    component: ErrorPageComponent
  },
  {
    path: 'reset/:username/:random',
    component: ResetComponent
  },
  {
    path: 'homeFacility',
    component: HomeFacilityComponent,
    canActivate: [AuthGuard, SummaryGuard],
    data: {
      breadcrumb: {
        name: 'SUMMARY.TITLE',
        translate: true,
        parents: [{
          name: "Home",
          path: "home"
        }]
      }
    }
  },
  {
    path: 'analysis',
    component: AnalysisComponent,
    canActivate: [AuthGuard, AnalysisGuard],
    data: {
      breadcrumb: {
        name: 'ANALYSIS.TITLE',
        translate: true,
        parents: [{
          name: "Home",
          path: "home"
        }]
      }
    }
  },
  {
    path: 'roi',
    component: RoiComponent,
    canActivate: [AuthGuard],
    data: {
      breadcrumb: {
        name: 'ROI',
        parents: [{
          name: "Home",
          path: "home"
        }]
      }
    }
  },
  {
    path: 'mdi',
    component: MdiComponent,
    canActivate: [AuthGuard],
    data: {
      breadcrumb: {
        name: 'MDI',
        parents: [{
          name: "Home",
          path: "home"
        }]
      }
    }
  },
  {
    path: 'ppr',
    component: PprComponent,
    canActivate: [AuthGuard],
    data: {
      breadcrumb: {
        name: 'PPR',
        parents: [{
          name: "Home",
          path: "home"
        }]
      }
    }
  },
  {
    path: 'survey',
    component: SurveyComponent,
    canActivate: [AuthGuard, AuthSurveyGuard],
    data: {
      breadcrumb: {
        name: 'SURVEY.TITLE',
        translate: true,
        parents: [{
          name: "Home",
          path: "home"
        },
        {
          name: "ANALYSIS.TITLE",
          translate: true,
          path: "analysis"
        }]
      }
    }
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard, DashboardGuard],
    data: {
      breadcrumb: {
        name: 'Dashboard',
        parents: [{
          name: "Home",
          path: "home"
        },
        {
          name: "ANALYSIS.TITLE",
          translate: true,
          path: "analysis"
        }]
      }
    }
  },
  {
    path: 'notifications',
    component: NotificationsComponent,
    canActivate: [AuthGuard],
    data: {
      breadcrumb: {
        name: 'Notifications',
        parents: [{
          name: "Home",
          path: "home"
        }]
      }
    }
  },
  { path: '**', component: PageNotFoundComponent }
]

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LoginComponent,
    EqualValidatorDirective,
    NavbarComponent,
    ModalProfileComponent,
    ModalEditProfileComponent,
    CheckmailComponent,
    ErrorPageComponent,
    ModalForgotPasswordComponent,
    ResetComponent,
    GooglePlacesDirective,
    AnalysisComponent,
    FooterComponent,
    HomeFacilityComponent,
    SurveyComponent,
    DashboardComponent,
    ValuesPipe,
    ModalSurveyComponent,
    NotificationsComponent,
    RoiComponent,
    MdiComponent,
    PprComponent,
    ModalReadMDIComponent,
    ModalSurveyRoiComponent,
    ModalReadPprComponent,
    DragDropDirective,
    BreadcrumbComponent,
    ModalNotificationComponent,
    PageNotFoundComponent
  ],
  entryComponents: [
    ModalProfileComponent,
    ModalEditProfileComponent,
    ModalForgotPasswordComponent,
    ModalSurveyComponent,
    ModalReadMDIComponent,
    ModalSurveyRoiComponent,
    ModalReadPprComponent,
    ModalNotificationComponent
  ],
  imports: [
    RouterModule.forRoot(appRoutes),
    BrowserModule,
    BrowserAnimationsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatStepperModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatListModule,
    MatInputModule,
    MatRadioModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatTabsModule,
    MatTooltipModule,
    MatMenuModule,
    MatGridListModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ChartsModule,
    NgSelectModule,
    MatBadgeModule,
    AgmCoreModule.forRoot({
      apiKey: 'AIzaSyBhjXBIP1wOKkPjFi9yjXFog6Dn8tm3ZwY',
      libraries: ["places"]
    }),
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    HttpModule,
    HttpClientModule,
    Ng2Webstorage,

    ToastrModule.forRoot({ positionClass: 'inline' }),
    ToastContainerModule,
    BrowserAnimationsModule,


    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),

    NgbModule,
    NgbModule.forRoot(),
    InfiniteScrollModule,
    SidebarModule.forRoot()
  ],
  providers: [
    UserService,
    FacilitiesService,
    PreviousRouteServiceService,
    SurveyService,
    AuthGuard,
    SummaryGuard,
    AnalysisGuard,
    AuthSurveyGuard,
    DashboardGuard,
    LoginComponent,
    HomeFacilityComponent,
    ModalProfileComponent,
    ModalEditProfileComponent,
    ModalForgotPasswordComponent,
    ModalSurveyComponent,
    NgbActiveModal,
    ValuesPipe,
    MatDatepickerModule,
    ErrorHandlerService,
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
